import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextField } from "@/components/wizard/Fields";
import LinksSection, { EMPTY_LINKS } from "@/components/wizard/LinksSection";
import { errMsg } from "@/lib/api";
import { useEditor } from "./useEditorState";
import { AiPrivacyNotice } from "./AiPrivacyNotice";

const FIELDS = [["description", "Descrizione dell'attività"], ["services", "Servizi o prodotti principali"], ["audience", "Pubblico di riferimento"],
  ["goal", "Obiettivo della demo"], ["required_pages", "Pagine o flussi richiesti"]];
const VISUAL = [["primary_color", "Colore principale"], ["secondary_colors", "Colori secondari"], ["font", "Font"], ["references", "Riferimenti visivi"], ["notes", "Indicazioni aggiuntive"]];

// The brief saved by the wizard, editable later: used by the next full AI generation (the current site is not touched).
export default function BriefDialog({ open, onOpenChange, onSavedGenerate }) {
  const { project, setProject, api, setWhatsapp, content } = useEditor();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const b = project.brief || {};
    const links = b.links ? { ...b.links, whatsapp: { ...(b.links.whatsapp || {}), bubble: !!content.whatsapp?.enabled } } : EMPTY_LINKS;  // the bubble shows the site's current state
    setF({ sector: project.sector || "", ...Object.fromEntries(FIELDS.map(([k]) => [k, b[k] || ""])), visual: { ...(b.visual || {}) }, links });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return null;
  const save = async (gen) => {
    setBusy(true);
    try {
      const { sector, visual, ...rest } = f;
      await api.patch(`/projects/${project.id}`, { sector, brief: { ...rest, visual } });
      const { data: fresh } = await api.get(`/projects/${project.id}`);
      setProject((p) => ({ ...p, sector, brief: fresh.brief || { ...(p.brief || {}), ...rest, visual } }));
      const w = fresh.content?.whatsapp;  // the server updated number/message: keep the editor's copy in sync so autosave doesn't revert it
      if (w) setWhatsapp({ phone: w.phone, message: w.message, enabled: w.enabled });
      toast.success("Brief salvato");
      onOpenChange(false);
      if (gen) onSavedGenerate?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setV = (k, v) => setF({ ...f, visual: { ...f.visual, [k]: v } });
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="brief-dialog">
        <DialogHeader><DialogTitle>Brief del sito</DialogTitle>
          <DialogDescription>Quello compilato nel wizard. Le modifiche valgono per la prossima generazione AI; il sito attuale non cambia finché non generi.</DialogDescription></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2"><AiPrivacyNotice testid="brief-ai-privacy-notice" /></div>
          <p className="md:col-span-2 text-xs text-slate-500">Azienda: <b className="text-slate-800">{project.company_name || "—"}</b> · tipologie e lingua si cambiano nel pannello «Progetto».</p>
          <TextField label="Settore" value={f.sector} onChange={(v) => setF({ ...f, sector: v })} testId="brief-edit-sector" />
          {FIELDS.map(([k, l]) => <TextField key={k} className={k === "description" || k === "required_pages" ? "md:col-span-2" : ""} multiline label={l} value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} testId={`brief-edit-${k}`} />)}
          {VISUAL.map(([k, l]) => <TextField key={k} multiline={k === "references" || k === "notes"} label={k === "references" ? "Riferimenti visivi (solo ispirazione per l'IA)" : l} value={f.visual[k] || ""} onChange={(v) => setV(k, v)} testId={`brief-edit-visual-${k}`} />)}
          <LinksSection links={f.links} onChange={(v) => setF({ ...f, links: v })} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="brief-edit-cancel">Annulla</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={busy} data-testid="brief-edit-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Salva brief</Button>
          {onSavedGenerate && <Button onClick={() => save(true)} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="brief-edit-save-generate">Salva e genera sito…</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}
